import { GenerateAdError, postGenerateAd } from '../../lib/generateAdRequest';
import {
  pollGeneratedByExecutionId,
  IMAGE_POLL_TIMEOUT_MS,
} from './pollGeneratedByExecutionId';

// Recuperación de generaciones de imagen de Interfaz V2 frente a cortes (se
// duerme la compu / se pierde la conexión / se recarga la página).
//
//  • A — `awaitImageGeneration`: corre la RESPUESTA del webhook y el POLL por
//    execution_id EN PARALELO ("carrera") y se queda con el primero que entregue
//    imágenes. NO usa ningún timeout que bloquee: si el fetch se cuelga (ej. la
//    compu se durmió a mitad), el poll igual resuelve apenas la imagen final
//    aparece en la DB —en ~segundos al volver—, sin esperas raras.
//
//  • B — `markPendingGeneration` / `readPendingGeneration` /
//    `clearPendingGeneration`: persisten el `execution_id` en `localStorage` al
//    arrancar una generación, para REANUDAR el poll si la página se recargó /
//    descartó (el poll en memoria muere ahí). Lo consume un efecto al montar.

export interface ImageGenerationResult {
  imageIds: string[];
  companyId: string;
}

interface WebhookResponse {
  imagenes_generadas_ids?: Array<string | number>;
  id_company?: string;
  [k: string]: unknown;
}

// ─── A — Carrera respuesta vs poll ───────────────────────────────────────
//
// Reglas:
//  • Error ACCIONABLE del webhook (policy/refusal) → corta y rechaza (no se
//    recupera: el contenido no se va a generar).
//  • Error técnico / respuesta sin ids → se ignora; decide el poll.
//  • El poll termina SIN imágenes OK (agotó su tope) → rechaza (no hay nada que
//    esperar). El tope del poll (5 min) es la cota del PEOR caso —"no se generó
//    nada"—, no del caso feliz ni del "volví y la imagen ya está".
export async function awaitImageGeneration(opts: {
  fetchPromise: Promise<WebhookResponse>;
  executionId: string;
  companyId: string;
  expectedCount?: number;
  pollTimeoutMs?: number;
  /** Override de la tolerancia de estabilidad del poll (rondas sin filas
   *  nuevas). Para flujos con gaps largos entre imágenes (anuncio/gpt-5). */
  pollStableRounds?: number;
}): Promise<ImageGenerationResult> {
  const { fetchPromise, executionId, companyId, expectedCount } = opts;
  const ac = new AbortController();

  return await new Promise<ImageGenerationResult>((resolve, reject) => {
    let done = false;
    const succeed = (imageIds: string[], cid: string) => {
      if (done) return;
      done = true;
      ac.abort(); // frenamos el otro camino (el poll deja de consultar la DB)
      resolve({ imageIds, companyId: cid });
    };
    const failHard = (err: unknown) => {
      if (done) return;
      done = true;
      ac.abort();
      reject(err);
    };

    // Camino 1: respuesta del webhook (rápido en el caso normal).
    fetchPromise.then(
      (d) => {
        const ids = (d?.imagenes_generadas_ids ?? []) as Array<string | number>;
        if (ids.length > 0) succeed(ids.map(String), d?.id_company || companyId);
        // respuesta sin ids → NO finalizamos: dejamos que el poll decida.
      },
      (err) => {
        if (err instanceof GenerateAdError && err.isActionable) failHard(err);
        // error técnico → no fatal; gana el poll.
      },
    );

    // Camino 2: poll por execution_id (sobrevive a que el fetch se cuelgue).
    pollGeneratedByExecutionId({
      executionId,
      companyId,
      expectedCount,
      timeoutMs: opts.pollTimeoutMs ?? IMAGE_POLL_TIMEOUT_MS,
      stableRounds: opts.pollStableRounds,
      signal: ac.signal,
    })
      .then((rows) => {
        const ok = rows.filter((r) => !r.has_error);
        if (ok.length > 0) succeed(ok.map((r) => r.id), companyId);
        // Poll terminó sin imágenes OK. Si lo abortó el otro camino, `done` ya
        // es true y esto es no-op; si no, no hay nada que esperar → fallamos.
        else failHard(new Error('No se generó ninguna imagen. Probá de nuevo.'));
      })
      .catch(() => {
        // El poll no rechaza por abort (devuelve filas), pero por las dudas
        // lo dejamos como no-op: si nadie resolvió, el camino del fetch decide.
      });
  });
}

// ─── A' — Atajo para el funnel CLÁSICO ───────────────────────────────────
//
// Las pantallas clásicas (elegir productos/composición/fondo, resultados,
// "nuevo modo") NO mandan execution_id ni pollean: hacían `postGenerateAd` y
// tomaban "ids vacíos" como error. Pero desde la migración esos modos rutean
// al backend nuevo, que ante una generación lenta responde 202 SIN ids (la
// termina el poll por execution_id) — así se perdía la generación en pantalla
// aunque quedara en el historial. Este helper genera el execution_id, corre la
// carrera respuesta-vs-poll y devuelve las imágenes por el camino que gane.
// Igual que las pantallas ya migradas (AnuncioFromImageSheet, AddFirstProduct).
export async function generateClassicImages(opts: {
  payload: Record<string, unknown>;
  companyId: string;
  /** Cantidad de imágenes esperadas (para cerrar el poll apenas llegan todas).
   *  Omitir en video/desconocido → el poll cierra por estabilidad. */
  expectedCount?: number;
  pollTimeoutMs?: number;
  pollStableRounds?: number;
}): Promise<ImageGenerationResult> {
  const executionId = crypto.randomUUID();
  return await awaitImageGeneration({
    // execution_id va en una COPIA fresca del payload: el original (lastPayload
    // de "Generar más") no debe arrastrar un id repetido entre generaciones.
    fetchPromise: postGenerateAd({
      ...opts.payload,
      execution_id: executionId,
    }) as Promise<{ imagenes_generadas_ids?: Array<string | number>; id_company?: string }>,
    executionId,
    companyId: opts.companyId,
    expectedCount: opts.expectedCount,
    pollTimeoutMs: opts.pollTimeoutMs,
    pollStableRounds: opts.pollStableRounds,
  });
}

// ─── B — Persistencia para reanudar tras recarga / descarte ──────────────

const PENDING_KEY = 'atlas-v2-pending-generation';
// Si la entrada es más vieja que esto, se descarta (la imagen, si salió, ya
// está en el historial). Evita reanudar generaciones "fantasma".
export const PENDING_MAX_AGE_MS = 10 * 60 * 1000;

export interface PendingGeneration {
  /** Uno o varios ids: los modos de catálogo usan un id POR LLAMADA desde
   *  ADR-590, así que la tanda pendiente puede tener N. */
  executionId: string | string[];
  companyId: string;
  expectedCount?: number;
  startedAt: number;
}

export function markPendingGeneration(p: {
  executionId: string | string[];
  companyId: string;
  expectedCount?: number;
}): void {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ ...p, startedAt: Date.now() }),
    );
  } catch {
    /* localStorage puede fallar en modo privado — non-fatal */
  }
}

export function clearPendingGeneration(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* noop */
  }
}

/** Devuelve la generación pendiente si existe y es RECIENTE (< MAX_AGE). Si es
 *  vieja o inválida, la limpia y devuelve null. */
export function readPendingGeneration(): PendingGeneration | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingGeneration;
    const idsOk = Array.isArray(p?.executionId)
      ? p.executionId.length > 0
      : !!p?.executionId;
    if (!idsOk || !p?.companyId) {
      clearPendingGeneration();
      return null;
    }
    if (Date.now() - (p.startedAt || 0) > PENDING_MAX_AGE_MS) {
      clearPendingGeneration();
      return null;
    }
    return p;
  } catch {
    return null;
  }
}
